import {IssueRelativeTime} from '@github-ui/ago'
import activityHeaderStyles from '@github-ui/commenting/ActivityHeaderStyles.module.css'
import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {CommentAuthorAssociation} from '@github-ui/commenting/CommentAuthorAssociation'
import {SpammyBadge} from '@github-ui/commenting/SpammyBadge'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {HovercardAttributesResult} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {
  MarkdownEditHistoryViewer,
  type MarkdownEditHistoryViewerCommentFragment,
} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {
  MarkdownLastEditedBy,
  type MarkdownLastEditedByFragment,
} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import {Label, Link} from '@primer/react'
import {clsx} from 'clsx'

import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import type {
  IssueBodyHeaderAuthor as IssueBodyHeaderAuthorNode,
  IssueBodyHeaderNode,
} from './issue-body-header-query-types'
import headerStyles from './IssueBodyHeader.module.css'
import {IssueBodyHeaderActions, type IssueBodyHeaderActionsProps} from './IssueBodyHeaderActions'
import {getIssueAuthorDisplayName, IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

type IssueBodyHeaderIssueSecondaryNode = NonNullable<
  NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']
>

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay parent
// data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueBodyHeaderSecondaryFragment_issueFragment = RelayCompat<{
  readonly repository: Pick<IssueBodyHeaderIssueSecondaryNode['repository'], 'name' | 'isPrivate'> & {
    readonly owner: Pick<IssueBodyHeaderIssueSecondaryNode['repository']['owner'], 'login'>
  }
  readonly viaApp: Pick<NonNullable<IssueBodyHeaderIssueSecondaryNode['viaApp']>, 'name' | 'slug' | 'url'> | null
}>

// Reuses the already-migrated leaf fragment types; adds the badge/association fields this header reads directly.
// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): the RelayCompat wrapper (and open-enum `authorAssociation`)
// only exists while the still-Relay parent feeds this. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueBodyHeaderSecondaryFragment = MarkdownEditHistoryViewerCommentFragment &
  MarkdownLastEditedByFragment &
  RelayCompat<Pick<IssueBodyHeaderIssueSecondaryNode, 'showSpammyBadge' | 'authorAssociation'>>

export type IssueBodyHeaderProps = {
  additionalActions?: React.ReactNode
  comment: IssueBodyHeaderFragment
  url?: string
  isPullRequest?: boolean
  actionProps?: IssueBodyHeaderActionsProps
  secondaryKey?: IssueBodyHeaderSecondaryFragment
  secondaryKeyIssue?: IssueBodyHeaderSecondaryFragment_issueFragment
  authorHovercardAttrs?: HovercardAttributesResult
}

// RELAY-CLEANUP(IssueBody): widens nullable fields so the still-Relay parent data is assignable.
// Remove when IssueBody leaves Relay.
export type IssueBodyHeaderFragment = RelayCompat<
  Pick<IssueBodyHeaderNode, 'body' | 'createdAt' | 'viewerDidAuthor'> & {
    readonly author:
      | (Pick<IssueBodyHeaderAuthorNode, 'avatarUrl' | 'login' | 'id' | 'profileUrl' | '__typename'> & {
          readonly name?: string | null
          readonly isCopilot?: boolean
        })
      | null
  }
>

export function IssueBodyHeader({
  additionalActions,
  comment: data,
  url,
  isPullRequest,
  actionProps,
  secondaryKey: secondaryData,
  secondaryKeyIssue: secondaryIssueData,
  authorHovercardAttrs,
}: IssueBodyHeaderProps) {
  const isBot = data.author?.__typename === 'Bot'
  const isMannequin = data.author?.__typename === 'Mannequin'
  const viaAppData = secondaryIssueData?.viaApp
  const viaApp = isFeatureEnabled('issue_via_app_avatar') ? viaAppData : undefined

  const timeElement = <IssueRelativeTime datetime={data.createdAt} className={headerStyles.RelativeTime} />

  const subText = (
    <>
      <span>{isPullRequest ? 'created ' : 'opened '}</span>
      {url ? (
        <Link href={url} data-testid="issue-body-header-link" className={headerStyles.dateLink}>
          {timeElement}
        </Link>
      ) : (
        timeElement
      )}
      {viaApp && (
        <span>
          {' '}
          – with{' '}
          <Link inline href={viaApp.url ?? undefined}>
            {viaApp.slug === 'copilot-swe-agent' ? 'Copilot' : viaApp.name}
          </Link>
        </span>
      )}
      {/** As Pull Request Issue Body Descriptions show Author label to the right of created at date, we need to render this additional label */}
      {isPullRequest && <Label className={headerStyles.authorLabel}>Author</Label>}
    </>
  )

  const {avatarUrl, login, profileUrl} = data.author ?? VALUES.ghost
  const accessibleAuthorName = getIssueAuthorDisplayName(data.author) ?? `@${login}`

  return (
    <div
      className={clsx(
        headerStyles.IssueBodyHeaderContainer,
        isFeatureEnabled('issue_inline_avatars') && headerStyles.IssueBodyHeaderContainer_inlineAvatars,
        data.viewerDidAuthor ? headerStyles.viewerDidAuthor : headerStyles.viewerDidNotAuthor,
      )}
    >
      <div className={clsx(activityHeaderStyles.activityHeader, headerStyles.activityHeaderWrapper)}>
        <div
          className={clsx(
            !isFeatureEnabled('issue_inline_avatars') && styles.avatarInner,
            headerStyles.avatarContainer,
            isPullRequest && headerStyles.pullRequestAvatar,
          )}
        >
          <Link
            href={profileUrl ?? undefined}
            {...authorHovercardAttrs}
            aria-label={`${accessibleAuthorName}'s profile`}
            className={profileUrl ? styles.avatarLink : styles.avatarWithoutLink}
          >
            <GitHubAvatar size={24} src={avatarUrl} alt={accessibleAuthorName} />
          </Link>
        </div>

        <div className={clsx(activityHeaderStyles.narrowViewportWrapper, headerStyles.narrowViewportWrapper)}>
          <div className={headerStyles.titleSection}>
            <IssueBodyHeaderAuthor author={data.author || null} authorHovercardAttrs={authorHovercardAttrs} />
          </div>
          {(isBot || isMannequin) && <Label variant="secondary">{LABELS.authorLabel(isBot)}</Label>}
          <div className={clsx(activityHeaderStyles.footer, headerStyles.footerSection)}>{subText}</div>
        </div>

        <div className={clsx(activityHeaderStyles.narrowViewportWrapper, headerStyles.actionsWrapper)}>
          <div className={clsx(activityHeaderStyles.edits, headerStyles.editsSection)}>
            <MarkdownEditHistoryViewer
              editHistory={secondaryData}
              renderAnchor={() => <MarkdownLastEditedBy editInformation={secondaryData} />}
            />
          </div>
          <div className={headerStyles.badgesSection}>
            <div className={headerStyles.badgeGroup}>
              {secondaryData?.showSpammyBadge && <SpammyBadge />}
              {secondaryData && secondaryIssueData?.repository?.isPrivate === false && (
                <CommentAuthorAssociation
                  association={secondaryData.authorAssociation}
                  viewerDidAuthor={data.viewerDidAuthor}
                  org={secondaryIssueData.repository.owner?.login || ''}
                  repo={secondaryIssueData.repository.name}
                />
              )}
            </div>
            <div className={headerStyles.actionsSection}>
              {additionalActions}
              {actionProps && data.author && (
                <IssueBodyHeaderActions
                  url={actionProps.url}
                  comment={data}
                  issueBodyRef={actionProps.issueBodyRef}
                  onReplySelect={actionProps.onReplySelect}
                  startIssueBodyEdit={actionProps.startIssueBodyEdit}
                  onEditHover={actionProps.onEditHover}
                  viewerCanUpdate={actionProps.viewerCanUpdate}
                  viewerCanReport={actionProps.viewerCanReport}
                  viewerCanReportToMaintainer={actionProps.viewerCanReportToMaintainer}
                  viewerCanBlockFromOrg={actionProps.viewerCanBlockFromOrg}
                  viewerCanUnblockFromOrg={actionProps.viewerCanUnblockFromOrg}
                  viewerCanBlockPersonally={actionProps.viewerCanBlockPersonally}
                  viewerCanUnblockPersonally={actionProps.viewerCanUnblockPersonally}
                  issueId={actionProps.issueId}
                  owner={actionProps.owner}
                  repository={actionProps.repository}
                  ownerId={actionProps.ownerId}
                  ownerUrl={actionProps.ownerUrl}
                  author={data.author}
                  pendingBlock={actionProps.pendingBlock}
                  pendingUnblock={actionProps.pendingUnblock}
                  isDisabled={actionProps.isDisabled}
                  actionMenuPortalContainerName={actionProps.actionMenuPortalContainerName}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
